import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { LayoutDashboard, Trophy, BrainCircuit, Fish, History, FlaskConical, Wallet, Settings, ScrollText, KeyRound, Gauge, LogOut, Menu, Bell, Bitcoin } from "lucide-react";
import { usePoll } from "@/lib/usePoll";
import { useAuth } from "@/context/AuthContext";
import { api, num, ago } from "@/lib/api";
import { Dot } from "@/components/common";
import { EmergencyButton, LockedBanner } from "@/components/Emergency";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

const NAV = [
  ["/", "Dashboard", LayoutDashboard], ["/challenge", "Million Challenge", Trophy], ["/agents", "AI Command Center", BrainCircuit],
  ["/whales", "Whale Tracker", Fish], ["/trades", "Trade History", History], ["/backtest", "Backtesting", FlaskConical],
  ["/account", "Account & Kraken", Wallet], ["/settings", "Risk Settings", Settings], ["/settings/api-keys", "API Keys", KeyRound], ["/system", "System Health", Gauge], ["/audit", "Audit Log", ScrollText],
];

function Nav({ onPick }) {
  const { logout } = useAuth();
  const nav = useNavigate();
  return (
    <div className="flex flex-col h-full">
      <div className="px-5 h-16 flex items-center gap-2.5 border-b border-zinc-800/80">
        <div className="w-8 h-8 rounded bg-[#F7931A] flex items-center justify-center"><Bitcoin className="w-5 h-5 text-black" /></div>
        <div>
          <div className="font-display font-bold text-sm tracking-tight text-zinc-50 leading-none">ZERO DOUBT AI</div>
          <div className="font-mono text-[9px] text-zinc-500 tracking-[0.2em] mt-1">BTC · KRAKEN · LIVE</div>
        </div>
      </div>
      <nav className="flex-1 py-4 px-3 space-y-0.5 overflow-y-auto">
        {NAV.map(([to, label, Icon]) => (
          <NavLink key={to} to={to} end={to === "/" || to === "/settings"} onClick={onPick} data-testid={`nav-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
            className={({ isActive }) => `flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors ${isActive ? "bg-[#F7931A]/10 text-[#F7931A] border-l-2 border-[#F7931A]" : "text-zinc-400 hover:text-zinc-100 hover:bg-zinc-800/40"}`}>
            <Icon className="w-4 h-4" /> {label}
          </NavLink>
        ))}
      </nav>
      <div className="p-3 border-t border-zinc-800/80">
        <div className="px-3 py-2 font-mono text-[10px] text-zinc-500 leading-relaxed">NO TRADE IS BETTER<br />THAN A BAD TRADE.</div>
        <button data-testid="logout-button" onClick={async () => { await logout(); nav("/login"); }} className="w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm text-zinc-500 hover:text-zinc-100 hover:bg-zinc-800/40">
          <LogOut className="w-4 h-4" /> Sign out
        </button>
      </div>
    </div>
  );
}

function useAlerts(count) {
  const prev = useRef(null);
  useEffect(() => {
    if (prev.current !== null && count > prev.current) {
      api.get("/notifications").then((r) => {
        r.data.filter((n) => !n.read).slice(0, count - prev.current).forEach((n) => {
          const fn = n.level === "emergency" || n.level === "error" ? toast.error : toast;
          fn(n.message, { duration: 12000 });
          if ("Notification" in window && Notification.permission === "granted") new Notification("Zero Doubt AI", { body: n.message });
        });
      });
    }
    prev.current = count;
  }, [count]);
}

function Notifications({ count }) {
  const { data, reload } = usePoll("/notifications", 0);
  const [perm, setPerm] = useState(typeof Notification !== "undefined" ? Notification.permission : "unsupported");
  useAlerts(count);
  return (
    <Popover onOpenChange={(o) => { if (o) { reload(); api.post("/notifications/read"); } }}>
      <PopoverTrigger data-testid="notifications-button" className="relative p-2 rounded-md hover:bg-zinc-800/60 text-zinc-400">
        <Bell className="w-4 h-4" />
        {count > 0 && <span className="absolute -top-0.5 -right-0.5 bg-red-600 text-white text-[9px] font-mono rounded-full w-4 h-4 flex items-center justify-center">{count > 9 ? "9+" : count}</span>}
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 bg-[#12141D] border-zinc-800 p-0 max-h-96 overflow-auto" data-testid="notifications-panel">
        {perm === "default" && <button data-testid="enable-desktop-alerts" onClick={() => Notification.requestPermission().then(setPerm)} className="w-full text-left px-4 py-2.5 text-xs text-[#F7931A] border-b border-zinc-800 hover:bg-zinc-800/40">Enable desktop alerts for opportunities & trades</button>}
        {(data || []).length === 0 && <div className="p-4 text-sm text-zinc-500">No alerts.</div>}
        {(data || []).map((n) => (
          <div key={n.id} className="px-4 py-3 border-b border-zinc-800/70">
            <div className={`font-mono text-[10px] uppercase ${n.level === "emergency" || n.level === "error" ? "text-red-400" : n.level === "opportunity" ? "text-emerald-400" : "text-amber-400"}`}>{n.level} · {ago(n.ts)}</div>
            <div className="text-xs text-zinc-300 mt-1">{n.message}</div>
          </div>
        ))}
      </PopoverContent>
    </Popover>
  );
}

export default function Layout() {
  const { data: st, reload } = usePoll("/status", 5000);
  const [open, setOpen] = useState(false);
  const c = st?.connection;
  const t = st?.ticker;
  const entriesOk = c?.entries_allowed && !st?.locked && !st?.reconciliation?.blocks_entries;
  return (
    <div className="grain min-h-screen bg-[#090A0F]">
      <aside className="w-64 bg-[#0D0E15] border-r border-zinc-800/80 fixed left-0 top-0 bottom-0 z-30 hidden md:flex flex-col"><Nav /></aside>
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="left" className="p-0 w-64 bg-[#0D0E15] border-zinc-800"><Nav onPick={() => setOpen(false)} /></SheetContent>
      </Sheet>
      <header className="h-16 fixed top-0 right-0 left-0 md:left-64 z-40 bg-[#090A0F]/85 backdrop-blur-md border-b border-zinc-800/80 flex items-center justify-between px-3 md:px-6 gap-2">
        <div className="flex items-center gap-3 md:gap-6 min-w-0">
          <button className="md:hidden p-2 text-zinc-400" onClick={() => setOpen(true)} data-testid="mobile-menu-button"><Menu className="w-5 h-5" /></button>
          <div data-testid="header-btc-price" className="flex items-baseline gap-2 min-w-0">
            <span className="font-mono text-[10px] text-zinc-500 hidden sm:inline">BTC/USD</span>
            <span className="font-mono text-base sm:text-lg font-bold text-zinc-50 tabular-nums">{st?.price ? `$${num(st.price, 1)}` : "—"}</span>
            {t && <span className={`font-mono text-xs ${t.change_pct >= 0 ? "text-emerald-400" : "text-red-400"}`}>{t.change_pct >= 0 ? "+" : ""}{num(t.change_pct, 2)}%</span>}
          </div>
          <div data-testid="kraken-connection-status" className="hidden lg:flex items-center gap-4 font-mono text-[10px] uppercase tracking-wider text-zinc-400">
            <span className="flex items-center gap-1.5"><Dot ok={c?.websocket?.healthy} /> WS feed</span>
            <span className="flex items-center gap-1.5"><Dot ok={c?.spot_private?.configured ? c?.spot_private?.ok !== false : false} /> Spot API</span>
            <span className="flex items-center gap-1.5"><Dot ok={c?.futures_private?.configured ? c?.futures_private?.ok !== false : false} /> Futures API</span>
            <span className="flex items-center gap-1.5"><Dot ok={c?.whale_alert?.connected} /> Whale Alert</span>
            <span className={`px-2 py-0.5 rounded border ${entriesOk ? "border-emerald-600/40 text-emerald-400" : "border-red-700/50 text-red-400"}`} data-testid="entries-status">
              {st?.locked ? "LOCKED" : entriesOk ? "ENTRIES ALLOWED" : "ENTRIES BLOCKED"}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-2 sm:gap-3">
          <span data-testid="entries-status-mobile" className={`lg:hidden font-mono text-[9px] px-1.5 py-0.5 rounded border ${entriesOk ? "border-emerald-600/40 text-emerald-400" : "border-red-700/50 text-red-400"}`}>
            {st?.locked ? "LOCKED" : entriesOk ? "LIVE" : "BLOCKED"}
          </span>
          <Notifications count={st?.unread_notifications || 0} />
          <EmergencyButton onDone={reload} />
        </div>
      </header>
      <main className="pt-20 md:pl-64 pb-12 relative z-10">
        <div className="px-4 md:px-8 max-w-[1600px] mx-auto">
          <LockedBanner status={st} onDone={reload} />
          {st?.reconciliation?.blocks_entries && (
            <div data-testid="reconcile-banner" className="border border-red-800/60 bg-red-950/20 rounded-lg px-4 py-3 mb-6 text-sm text-red-300">
              <b className="font-mono uppercase text-xs tracking-wider">Live entries blocked — accounting reconciliation:</b> {st.reconciliation.message} <Link to="/system" className="underline text-[#F7931A]">Resolve in System Health</Link>
            </div>
          )}
          {st?.review_required && (
            <div data-testid="review-banner" className="border border-amber-600/50 bg-amber-500/10 rounded-lg px-4 py-3 mb-6 text-sm text-amber-300">
              <b className="font-mono uppercase text-xs tracking-wider">Strategy review required:</b> {st.review_reason} — complete it in Risk Settings.
            </div>
          )}
          <Outlet context={{ status: st, reloadStatus: reload }} />
        </div>
      </main>
    </div>
  );
}
